/**
 * Contact section with form - using original Portugal Portuguese text from old website
 */

import { useState } from 'react';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAnalytics } from '@/hooks/useAnalytics';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import contactData from '@/data/contact.json';
import generalData from '@/data/general.json';

const sectionTitle = contactData.section_title;
const sectionTitleParts = contactData.section_title_parts || [{ text: sectionTitle, highlight: false }];

const contactSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z.string().min(9, 'Telefone inválido'),
  location: z.string().optional(),
  projectType: z.string().optional(),
  message: z.string().optional()
});

type ContactFormData = z.infer<typeof contactSchema>;

const projectTypes = contactData.project_types || [];

export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0.1 });
  const { trackContactFormSubmit } = useAnalytics();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(contactSchema)
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Find the project type label for the selected value
      const projectTypeLabel = projectTypes.find(pt => pt.value === data.projectType)?.label || data.projectType;

      // Format phone number: add space after country code if missing
      // react-phone-input-2 outputs format like "+351912345678", we want "+351 912345678"
      let formattedPhone = data.phone;
      if (formattedPhone && formattedPhone.startsWith('+') && !formattedPhone.includes(' ')) {
        // Find where the country code ends (after the digits following +)
        const match = formattedPhone.match(/^\+\d+/);
        if (match) {
          const countryCode = match[0];
          const rest = formattedPhone.slice(countryCode.length);
          formattedPhone = `${countryCode} ${rest}`;
        }
      }

      // Submit to Formzero with Portuguese field names and formatted phone
      const response = await fetch('https://magreen-forms.alxmra.workers.dev/api/forms/magreen/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Nome: data.name,
          Email: data.email,
          Telefone: formattedPhone,
          Localidade: data.location,
          'Tipo de Projecto': projectTypeLabel,
          Mensagem: data.message
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Track analytics event
      trackContactFormSubmit();

      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} style={{
      background: 'linear-gradient(to bottom right, var(--color-lime-500), var(--color-lime-600))',
      color: '#ffffff',
      padding: '4rem 0'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isIntersecting ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="has-text-centered mb-6"
        >
          <h2 className="title is-2 has-text-weight-bold" style={{ color: '#ffffff' }}>
            {sectionTitleParts.map((part, index) => {
              if ((part as any).color === 'black') {
                return <span key={index} style={{ color: '#000000' }}>{part.text}</span>;
              }
              return part.highlight ? (
                <span key={index} style={{ color: 'var(--color-lime-400)' }}>{part.text}</span>
              ) : (
                <span key={index}>{part.text}</span>
              );
            })}
          </h2>
          <p className="subtitle is-5" style={{ maxWidth: '700px', margin: '0 auto', color: 'rgba(255, 255, 255, 0.9)' }}>
            {contactData.section_subtitle}
          </p>
        </motion.div>

        <div className="columns">
          {/* Contact Info */}
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isIntersecting ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ color: '#ffffff' }}
            >
              <h3 className="title is-3 has-text-weight-bold mb-6" style={{ color: '#ffffff' }}>
                {contactData.info_title}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{contactData.location_label}</div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.9)' }} dangerouslySetInnerHTML={{ __html: contactData.location_text.replace('\n', '<br />') }} />
                    {(contactData as any).maps_url && (contactData as any).maps_button_text && (
                      <a
                        href={(contactData as any).maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          transition: 'all 0.3s',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {(contactData as any).maps_button_text}
                      </a>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{contactData.phone_label}</div>
                    <a href={`tel:${generalData.phone.replace(/\s/g, '')}`} style={{ color: '#ffffff', transition: 'color 0.3s', fontSize: '1.25rem' }}>
                      {generalData.phone}
                    </a>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{contactData.email_label}</div>
                    <a href={`mailto:${generalData.email}`} style={{ color: '#ffffff', transition: 'color 0.3s' }}>
                      {generalData.email}
                    </a>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{contactData.hours_label}</div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.9)' }} dangerouslySetInnerHTML={{ __html: contactData.hours_text.replace('\n', '<br />') }} />
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div style={{ marginTop: '3rem', marginBottom: '3rem' }}>
                <h3 className="title is-4 has-text-weight-bold" style={{ color: '#ffffff', marginBottom: '1rem' }}>
                  {contactData.follow_us_title}
                </h3>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <a
                    href={generalData.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button"
                    style={{
                      width: '3rem',
                      height: '3rem',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    aria-label="Facebook"
                  >
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    href={generalData.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button"
                    style={{
                      width: '3rem',
                      height: '3rem',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    aria-label="Instagram"
                  >
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Coverage Badge */}
              <div className="card-magreen" style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '1rem',
                padding: '1.5rem'
              }}>
                <svg style={{ width: '40px', height: '40px', marginBottom: '1rem' }} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                <h3 className="title is-4 has-text-weight-bold mb-2" style={{ color: '#ffffff' }}>
                  {contactData.coverage_badge_title}
                </h3>
                <p style={{ fontSize: '1.125rem' }}>
                  {contactData.coverage_badge_text}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isIntersecting ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="card-magreen" style={{ background: '#ffffff', padding: '2rem', borderRadius: '1rem' }}>
                <h3 className="title is-3 has-text-weight-bold mb-6" style={{ color: 'var(--color-gray-800)' }}>
                  {contactData.form_title}
                </h3>

                <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="name" style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-gray-700)' }}>
                      {contactData.form_name_label} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      {...register('name')}
                      className="form-input"
                      placeholder={contactData.name_placeholder}
                    />
                    {errors.name && (
                      <p className="form-error">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-gray-700)' }}>
                      {contactData.form_email_label} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      {...register('email')}
                      className="form-input"
                      placeholder={contactData.email_placeholder}
                    />
                    {errors.email && (
                      <p className="form-error">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-gray-700)' }}>
                      {contactData.form_phone_label} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <PhoneInput
                          country={'pt'}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder={contactData.phone_placeholder}
                          inputStyle={{
                            width: '100%',
                            height: '45px',
                            fontSize: '1rem',
                            border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: '#ffffff'
                          }}
                          autoFormat={true}
                          enableSearch={true}
                          searchPlaceholder="Pesquisar país..."
                          searchNotFound="País não encontrado"
                          preferredCountries={['pt', 'es', 'fr', 'de', 'gb', 'it']}
                        />
                      )}
                    />
                    {errors.phone && (
                      <p className="form-error" style={{ marginTop: '0.25rem' }}>{errors.phone.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="location" style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-gray-700)' }}>
                      {contactData.location_field_label}
                    </label>
                    <input
                      id="location"
                      type="text"
                      {...register('location')}
                      className="form-input"
                      placeholder={contactData.location_field_placeholder}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="projectType" style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-gray-700)' }}>
                      {contactData.project_type_label}
                    </label>
                    <select
                      id="projectType"
                      {...register('projectType')}
                      className="form-input"
                    >
                      <option value="">{contactData.project_type_default}</option>
                      {projectTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-gray-700)' }}>
                      {contactData.message_label}
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      {...register('message')}
                      className="form-input"
                      placeholder={contactData.message_placeholder}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="button btn-magreen btn-large"
                    style={{ width: '100%', opacity: isSubmitting ? 0.5 : 1 }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin" style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {contactData.submit_button_sending}
                      </>
                    ) : (
                      <>
                        <svg style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        {contactData.submit_button_text}
                      </>
                    )}
                  </button>
                </form>

                {/* Status Messages */}
                <AnimatePresence>
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#dcfce7',
                        border: '1px solid #22c55e',
                        color: '#15803d',
                        borderRadius: '0.5rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{contactData.success_message}</span>
                      </div>
                    </motion.div>
                  )}
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#fee2e2',
                        border: '1px solid #ef4444',
                        color: '#b91c1c',
                        borderRadius: '0.5rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span>{contactData.error_message}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
